// ============================================================================
// StageRequestsDock.tsx
//
// « Monter sur le plateau » (2026-10-15), côté réalisateur : les spectateurs
// connectés de Kleon.tv demandent à devenir caméra invitée du direct.
//
// Un dock toujours visible plutôt qu'une section du panneau des sources : ce
// panneau est une fenêtre qu'on ouvre et qu'on ferme, alors qu'une demande
// arrive n'importe quand et n'attend que 10 minutes. Le dock compte les
// demandes, sonne à chaque nouvelle, et ouvre la liste au clic.
//
// Réservé aux chaînes créées par la régie web (createdFromRegie) : sur une
// chaîne née dans les apps, le dock ne paraît pas (consigne du 2026-10-15).
//
// Accepter crée la caméra de l'invité sur le serveur (admin-api) : elle arrive
// dans les sources distantes, étiquetée « (public) », EN COULISSES — rien ne
// passe à l'antenne sans que le réalisateur l'y mette.
//
// Salle d'attente (2026-10-16) : avant de répondre, le réalisateur voit la
// caméra de chaque spectateur en attente, en miniature à côté de son nom, en
// grand d'un clic sur la vidéo. Cliquer une demande la sélectionne : on
// n'entend qu'elle, et « Parler » ouvre le micro de la régie vers ce seul
// spectateur (useStagePreviews : WebRTC direct, rien ne part à l'antenne).
//
// Démo sans serveur : `?demoPlateau` dans l'adresse de la régie (mires animées
// à la place des caméras).
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Maximize2, Mic, MicOff, Minimize2, Users, Video, X } from 'lucide-react';

import {
  StageGuest, StagePendingRequest, StageRequestError, StageState,
  acceptStageRequest, fetchStageState, refuseStageRequest, removeStageGuest, setStageOpen,
} from '../../../backendServices/stageManagment/api_stageRequests';
import { playChime } from '../chatModerationManagment/chatModerationUi';
import { StagePreview, useStagePreviews } from './useStagePreviews';

interface Props {
  adminId          : number | undefined;
  channelId        : number | undefined;
  /** Faux pour une chaîne créée par les apps : le dock ne paraît pas. */
  createdFromRegie : boolean;
  /** Relit la liste des caméras de la régie après une arrivée ou un départ. */
  onGuestsChanged  : () => void;
  /** Alerte de la régie (quota plein, microservice injoignable). */
  showAlert        : (message: string, type?: 'important') => void;
}

/** Relevé toutes les 5 s, 20 s quand l'onglet est caché (même rythme que les signalements). */
const POLL_VISIBLE_MS = 5_000;
const POLL_HIDDEN_MS  = 20_000;
/** Des demandes attendent : relevé plus serré, pour répondre vite à l'offre de
 *  leur caméra (la salle d'attente s'ouvre au relevé qui suit). */
const POLL_PENDING_MS = 2_500;

const DEMO = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('demoPlateau');

const demoState = (): StageState => {
  const now = Date.now();
  const iso = (offsetMin: number) => new Date(now + offsetMin * 60_000).toISOString();
  return {
    channel    : { id: 0, active: true, createdFromRegie: true, stageOpen: true },
    serverTime : iso(0),
    pending    : [
      { id: 1, displayName: 'Camille',     message: 'Je voudrais réagir sur la question du logement étudiant.', createdAt: iso(-1), expiresAt: iso(9) },
      { id: 2, displayName: 'Yanis',       message: "J'ai une question pour le deuxième invité.",                createdAt: iso(-3), expiresAt: iso(7) },
      { id: 3, displayName: 'Marie-Laure', message: 'Témoignage : je suis propriétaire bailleuse.',              createdAt: iso(-4), expiresAt: iso(6) },
    ],
    onStage    : [],
  };
};

const sinceText = (isoDate: string | null, now: number): string => {
  if (!isoDate) return '';
  const minutes = Math.max(0, Math.round((now - new Date(isoDate).getTime()) / 60_000));
  return minutes < 1 ? "à l'instant" : `il y a ${minutes} min`;
};

const remainingText = (isoDate: string, now: number): string => {
  const seconds = Math.max(0, Math.round((new Date(isoDate).getTime() - now) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

/** Démo : une mire animée au nom du spectateur, à la place de sa caméra. */
const demoCameraStream = (name: string, hue: number): MediaStream | null => {
  const canvas = document.createElement('canvas');
  canvas.width = 640; canvas.height = 360;
  const context = canvas.getContext('2d');
  if (!context || typeof canvas.captureStream !== 'function') return null;
  const draw = (time: number) => {
    const shift = (Math.sin(time / 1500) + 1) * 20;
    const gradient = context.createLinearGradient(0, 0, 640, 360);
    gradient.addColorStop(0, `hsl(${hue}, 45%, ${22 + shift / 4}%)`);
    gradient.addColorStop(1, `hsl(${hue + 40}, 50%, ${12 + shift / 3}%)`);
    context.fillStyle = gradient;
    context.fillRect(0, 0, 640, 360);
    context.fillStyle = 'rgba(255,255,255,0.85)';
    context.beginPath();
    context.arc(320, 150 + Math.sin(time / 700) * 6, 62, 0, Math.PI * 2);
    context.fill();
    context.fillRect(220, 230, 200, 130);
    context.font = 'bold 34px system-ui, sans-serif';
    context.textAlign = 'center';
    context.fillText(name, 320, 60);
    requestAnimationFrame(draw);
  };
  requestAnimationFrame(draw);
  return canvas.captureStream(25);
};

/** Une vidéo branchée sur un flux (le son est joué à part, par useStagePreviews). */
const PreviewVideo: React.FC<{ stream: MediaStream; className: string }> = ({ stream, className }) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => { if (ref.current) ref.current.srcObject = stream; }, [stream]);
  return <video ref={ref} autoPlay muted playsInline className={className} />;
};

const previewStatusText = (preview: StagePreview | undefined): string => {
  if (!preview || preview.status === 'none') return 'Caméra pas encore ouverte';
  if (preview.status === 'lost') return 'Caméra perdue — reconnexion…';
  return 'Connexion à sa caméra…';
};

const StageRequestsDock: React.FC<Props> = ({ adminId, channelId, createdFromRegie, onGuestsChanged, showAlert }) => {
  const [state,   setState]   = useState<StageState | null>(DEMO ? demoState() : null);
  const [open,    setOpen]    = useState(false);
  const [busyId,  setBusyId]  = useState<number | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const [now,     setNow]     = useState(Date.now());

  // Salle d'attente : la demande sélectionnée (on n'entend qu'elle), celle dont la
  // vidéo est en grand, et le micro de la régie vers la sélectionnée.
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [enlargedId, setEnlargedId] = useState<number | null>(null);
  const [talking,    setTalking]    = useState(false);

  // Le bouton vit dans l'en-tête de l'onglet Régie, à gauche du nom de la chaîne
  // (BroadcastingChannelSheet pose `#stage-dock-slot` : seul l'onglet actif a son en-tête).
  // L'onglet Régie reste monté quand on le quitte : le dock continue de relever, et
  // son bouton réapparaît avec l'en-tête. Sans emplacement, il ne s'affiche pas.
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const find = () => setSlot(document.getElementById('stage-dock-slot'));
    find();
    const observer = new MutationObserver(find);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Ids déjà vus : seule une demande NOUVELLE sonne, pas chaque relevé.
  const seenIdsRef = useRef<Set<number> | null>(null);
  const pendingCountRef = useRef(0);

  const enabled = DEMO || (createdFromRegie && !!adminId && !!channelId);

  const refresh = useCallback(async () => {
    if (DEMO || !adminId || !channelId) return;
    try {
      const fresh = await fetchStageState(adminId, channelId);
      setState(fresh);
      setError(null);
      pendingCountRef.current = fresh.pending.length;

      const seen = seenIdsRef.current;
      const ids  = new Set(fresh.pending.map(r => r.id));
      if (seen && fresh.pending.some(r => !seen.has(r.id))) playChime();
      seenIdsRef.current = ids;
    } catch (err) {
      // 404 : admin-api pas encore déployé. Le dock se tait plutôt que d'alerter
      // à chaque relevé ; la ligne d'état le dit quand on l'ouvre.
      setError(err instanceof StageRequestError && err.status === 404
        ? "Le serveur ne connaît pas encore « Monter sur le plateau » (admin-api à déployer)."
        : (err as Error).message);
    }
  }, [adminId, channelId]);

  // Relevé dont le délai se reprogramme (onglet visible ou caché).
  useEffect(() => {
    if (!enabled) return;
    let timer: number | undefined;
    let cancelled = false;
    const tick = async () => {
      await refresh();
      if (cancelled) return;
      timer = window.setTimeout(tick, document.hidden ? POLL_HIDDEN_MS : pendingCountRef.current > 0 ? POLL_PENDING_MS : POLL_VISIBLE_MS);
    };
    void tick();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [enabled, refresh]);

  // Les comptes à rebours du panneau.
  useEffect(() => {
    if (!open) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [open]);

  const pending = useMemo(
    () => (state?.pending ?? []).filter(r => new Date(r.expiresAt).getTime() > now),
    [state, now],
  );
  const onStage = state?.onStage ?? [];
  const stageOpen = state?.channel.stageOpen ?? false;

  // ── Salle d'attente ─────────────────────────────────────────────────────

  const onMicError = useCallback((message: string) => {
    setTalking(false);
    showAlert(message, 'important');
  }, [showAlert]);

  const livePreviews = useStagePreviews(adminId, DEMO ? [] : pending, selectedId, talking, onMicError);

  // Démo : une mire par demande, créée une fois.
  const demoStreamsRef = useRef(new Map<number, MediaStream | null>());
  const previews = useMemo<Record<number, StagePreview>>(() => {
    if (!DEMO) return livePreviews;
    const result: Record<number, StagePreview> = {};
    pending.forEach((request, index) => {
      if (!demoStreamsRef.current.has(request.id)) {
        demoStreamsRef.current.set(request.id, demoCameraStream(request.displayName, 200 + index * 70));
      }
      const stream = demoStreamsRef.current.get(request.id) ?? null;
      result[request.id] = { stream, status: stream ? 'connected' : 'none' };
    });
    return result;
  }, [livePreviews, pending]);

  // La demande sélectionnée s'en va (acceptée, refusée, expirée, annulée) : le
  // micro se coupe avec elle.
  useEffect(() => {
    if (selectedId !== null && !pending.some(r => r.id === selectedId)) {
      setSelectedId(null);
      setTalking(false);
    }
    if (enlargedId !== null && !pending.some(r => r.id === enlargedId)) setEnlargedId(null);
  }, [pending, selectedId, enlargedId]);

  // Panneau fermé : jamais de micro ouvert qu'on ne voit plus.
  useEffect(() => { if (!open) setTalking(false); }, [open]);

  const select = (id: number) => {
    if (id !== selectedId) setTalking(false);
    setSelectedId(id);
  };

  const toggleEnlarged = (id: number) => {
    select(id);
    setEnlargedId(current => (current === id ? null : id));
  };

  if (!enabled) return null;

  // ── Gestes ──────────────────────────────────────────────────────────────

  const toggleOpenToPublic = async () => {
    const next = !stageOpen;
    if (DEMO) { setState(s => s && { ...s, channel: { ...s.channel, stageOpen: next }, pending: next ? s.pending : [] }); return; }
    if (!adminId || !channelId) return;
    try {
      await setStageOpen(adminId, channelId, next);
      await refresh();
    } catch (err) {
      showAlert(`Le plateau n'a pas pu être ${next ? 'ouvert' : 'fermé'} : ${(err as Error).message}`, 'important');
    }
  };

  const accept = async (request: StagePendingRequest) => {
    if (DEMO) {
      setState(s => s && {
        ...s,
        pending : s.pending.filter(r => r.id !== request.id),
        onStage : [...s.onStage, { id: request.id, displayName: request.displayName, message: request.message, since: new Date().toISOString(), deviceId: `${request.displayName.toLowerCase()}-public` }],
      });
      return;
    }
    if (!adminId) return;
    setBusyId(request.id);
    try {
      await acceptStageRequest(adminId, request.id);
      onGuestsChanged();
      await refresh();
    } catch (err) {
      const e = err as StageRequestError;
      showAlert(
        e.code === 'full'
          ? "Le plateau est complet : toutes les places de caméra du compte sont prises. Retirez une caméra ou augmentez votre quota d'appareils."
          : `La demande n'a pas pu être acceptée : ${e.message}`,
        'important',
      );
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  const refuse = async (request: StagePendingRequest) => {
    if (DEMO) { setState(s => s && { ...s, pending: s.pending.filter(r => r.id !== request.id) }); return; }
    if (!adminId) return;
    setBusyId(request.id);
    try { await refuseStageRequest(adminId, request.id); await refresh(); }
    catch (err) { showAlert(`Refus impossible : ${(err as Error).message}`, 'important'); }
    finally { setBusyId(null); }
  };

  const remove = async (guest: StageGuest) => {
    if (DEMO) { setState(s => s && { ...s, onStage: s.onStage.filter(g => g.id !== guest.id) }); return; }
    if (!adminId) return;
    setBusyId(guest.id);
    try { await removeStageGuest(adminId, guest.id); onGuestsChanged(); await refresh(); }
    catch (err) { showAlert(`Retrait impossible : ${(err as Error).message}`, 'important'); }
    finally { setBusyId(null); }
  };

  // ── Rendu ───────────────────────────────────────────────────────────────

  const initial = (name: string) => (name.trim()[0] ?? '?').toUpperCase();

  return (
    <>
      {/* Le dock : fermé, il ne dit que l'essentiel. */}
      {slot && createPortal(
      <button
        onClick={() => setOpen(v => !v)}
        title="Monter sur le plateau : demandes du public"
        className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1 text-sm font-semibold shadow-lg transition-colors ${
          pending.length > 0
            ? 'bg-amber-500 text-slate-900 border-amber-300 animate-pulse'
            : stageOpen
              ? 'bg-slate-800 text-white border-slate-600 hover:bg-slate-700'
              : 'bg-slate-900/80 text-slate-400 border-slate-700 hover:text-slate-200'}`}
      >
        <Users className="w-4 h-4" />
        Public
        {pending.length > 0 && <span className="rounded-full bg-slate-900 text-amber-300 px-2 text-xs">{pending.length}</span>}
        {onStage.length > 0 && <span className="text-xs opacity-80">· {onStage.length} sur le plateau</span>}
      </button>,
      slot)}

      {open && slot && (
        // Plus large quand une vidéo est en grand.
        <div className={`fixed top-24 right-4 z-50 ${enlargedId !== null ? 'w-[560px]' : 'w-[440px]'} max-w-[calc(100vw-2rem)] max-h-[75vh] overflow-y-auto rounded-xl border border-slate-700 bg-slate-800 p-4 shadow-2xl text-white`}>
          <div className="flex items-center gap-3 mb-1">
            <Video className="w-5 h-5 text-amber-400" />
            <h3 className="flex-1 font-bold">Demandes du public <span className="text-amber-400">({pending.length})</span></h3>
            <button onClick={() => setOpen(false)} aria-label="Fermer" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          {/* Le réglage « Ouvrir le plateau au public », le même que l'onglet Configuration. */}
          <div className="flex items-center gap-3 mt-3 mb-1">
            <span className="flex-1 text-sm text-slate-300">Ouvrir le plateau au public</span>
            <button
              role="switch"
              aria-checked={stageOpen}
              aria-label="Ouvrir le plateau au public"
              onClick={toggleOpenToPublic}
              className={`w-12 h-7 rounded-full p-1 flex transition-colors ${stageOpen ? 'bg-blue-500 justify-end' : 'bg-slate-600 justify-start'}`}
            >
              <span className="w-5 h-5 rounded-full bg-white" />
            </button>
          </div>
          <p className="text-xs text-slate-400 mb-3">
            {stageOpen
              ? state?.channel.active === false
                ? 'La chaîne est éteinte : le bouton paraîtra sur Kleon.tv dès son allumage.'
                : 'Le bouton « Monter sur le plateau » est visible sur Kleon.tv pour ce direct.'
              : 'Fermé : les spectateurs ne voient plus le bouton. Les demandes en cours sont refusées poliment.'}
          </p>

          {error && <p className="mb-3 rounded bg-orange-900/30 border border-orange-500/40 px-3 py-2 text-xs text-orange-300">{error}</p>}

          <div className="space-y-2">
            {pending.length > 0 && (
              <p className="text-xs text-slate-400">
                Cliquez une demande pour l'écouter et lui parler, sa vidéo pour l'agrandir. Rien de tout cela ne part à l'antenne.
              </p>
            )}
            {pending.map(request => {
              const preview  = previews[request.id];
              const stream   = preview?.stream ?? null;
              const selected = selectedId === request.id;
              const enlarged = enlargedId === request.id && stream !== null;
              const speaking = selected && talking;
              return (
              <div
                key={request.id}
                onClick={() => select(request.id)}
                className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                  speaking ? 'border-rose-500 bg-rose-950/30'
                  : selected ? 'border-amber-400 bg-slate-900/80'
                  : 'border-slate-700 bg-slate-900/60 hover:border-slate-500'}`}
              >
                {/* En grand : la vidéo prend toute la largeur, au-dessus du nom. */}
                {enlarged && stream && (
                  <button
                    onClick={event => { event.stopPropagation(); toggleEnlarged(request.id); }}
                    title="Réduire la vidéo"
                    className="group relative mb-3 block w-full overflow-hidden rounded-md bg-black"
                  >
                    <PreviewVideo stream={stream} className="aspect-video w-full object-cover" />
                    <span className="absolute right-2 top-2 rounded bg-black/60 p-1 opacity-70 group-hover:opacity-100"><Minimize2 className="w-4 h-4" /></span>
                    {speaking && <span className="absolute left-2 top-2 rounded bg-rose-600 px-2 py-0.5 text-[10px] font-extrabold tracking-wide">MICRO OUVERT</span>}
                  </button>
                )}

                <div className="flex items-center gap-3">
                  {/* La miniature, à côté du nom ; l'initiale tant que la caméra n'est pas là. */}
                  {stream && !enlarged ? (
                    <button
                      onClick={event => { event.stopPropagation(); toggleEnlarged(request.id); }}
                      title="Agrandir la vidéo"
                      className="group relative w-28 shrink-0 overflow-hidden rounded-md bg-black"
                    >
                      <PreviewVideo stream={stream} className="aspect-video w-full object-cover" />
                      <span className="absolute right-1 top-1 rounded bg-black/60 p-0.5 opacity-0 group-hover:opacity-100"><Maximize2 className="w-3 h-3" /></span>
                    </button>
                  ) : !stream ? (
                    <div className="w-9 h-9 shrink-0 rounded-full bg-violet-600 flex items-center justify-center font-bold">{initial(request.displayName)}</div>
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{request.displayName}</div>
                    <div className="text-xs text-slate-400">{sinceText(request.createdAt, now)} · expire dans {remainingText(request.expiresAt, now)}</div>
                    {!stream && <div className="text-xs text-slate-500">{previewStatusText(preview)}</div>}
                    {stream && selected && !speaking && <div className="text-xs text-amber-300">Vous l'écoutez</div>}
                    {speaking && <div className="text-xs font-semibold text-rose-300">{request.displayName} vous entend</div>}
                  </div>
                  {/* L'intercom : seulement vers la demande sélectionnée, caméra connectée. */}
                  {stream && (
                    <button
                      onClick={event => {
                        event.stopPropagation();
                        if (!selected) { select(request.id); setTalking(true); return; }
                        setTalking(v => !v);
                      }}
                      title={speaking ? 'Couper le micro' : `Parler à ${request.displayName}`}
                      className={`flex shrink-0 items-center gap-1 rounded-md px-3 py-2 text-xs font-bold ${
                        speaking ? 'bg-rose-600 hover:bg-rose-500 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-100'}`}
                    >
                      {speaking ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                      {speaking ? 'Couper' : 'Parler'}
                    </button>
                  )}
                </div>
                {request.message && <p className="mt-2 text-sm text-slate-200">« {request.message} »</p>}
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button disabled={busyId === request.id} onClick={event => { event.stopPropagation(); void refuse(request); }}
                    className="rounded-md bg-slate-700 hover:bg-slate-600 py-2 text-sm font-semibold disabled:opacity-50">Refuser</button>
                  <button disabled={busyId === request.id} onClick={event => { event.stopPropagation(); void accept(request); }}
                    className="rounded-md bg-emerald-400 hover:bg-emerald-300 text-emerald-950 py-2 text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-50">
                    <Check className="w-4 h-4" />{busyId === request.id ? 'Création…' : 'Accepter'}
                  </button>
                </div>
              </div>
              );
            })}
            {pending.length === 0 && (
              <p className="rounded-lg bg-slate-900/40 p-4 text-center text-sm text-slate-400">Aucune demande en attente.</p>
            )}
          </div>

          <h4 className="mt-5 mb-1 font-semibold">Sur le plateau</h4>
          <p className="text-xs text-slate-400 mb-2">Les invités du public arrivent dans vos sources distantes, étiquetés « (public) », en coulisses : à vous de les passer à l'antenne.</p>
          <div className="space-y-2">
            {onStage.map(guest => (
              <div key={guest.id} className="flex items-center gap-3 rounded-lg border border-slate-700 bg-slate-900/60 p-3">
                <span className="rounded bg-violet-600 px-2 py-0.5 text-[10px] font-extrabold tracking-wide">PUBLIC</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{guest.displayName}</div>
                  <div className="text-xs text-amber-400">Caméra invitée · arrivée {sinceText(guest.since, now)}</div>
                </div>
                <button disabled={busyId === guest.id} onClick={() => remove(guest)}
                  className="rounded-md border border-rose-500/60 px-3 py-1.5 text-xs font-semibold text-rose-300 hover:bg-rose-500/10 disabled:opacity-50">Retirer</button>
              </div>
            ))}
            {onStage.length === 0 && (
              <p className="rounded-lg bg-slate-900/40 p-3 text-sm text-slate-400">Personne du public pour l'instant.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StageRequestsDock;
